// Yeni Proje — plan §4.3.4 + design ref #screen-project-new.
// 4 adımlı wizard: (1) Framework kart grid (2) Yıl + dönem (3) Danışman ata (4) Onayla.
// ?clientId=... query param — ClientProfile'dan gelindiyse müşteri önceden seçili.
// Raporlama dönemi (Task 11 / 1C-6A): dönem segmenti YALNIZ framework
// supportsQuarterly iken görünür; kapalıysa adım 2 bugünküyle birebir aynıdır.

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  formatReportingPeriod,
  type PeriodType,
  type ProjectsListResponse,
  type PublishedFrameworkEntry,
  type PublishedFrameworksResponse,
  type SectorPackageSummary,
  type UserListItem,
  type UsersListResponse,
  roleDisplayName,
} from "@mahir/shared";
import { api, ApiError } from "../api/client";
import { getClientById, toPickerItem } from "../api/clients";
import { ClientPicker } from "../components/common/ClientPicker";
import type { ClientPickerItem } from "../lib/client-picker";
import { listPublishedSectorPackages } from "../api/sector-packages";
import { listKpiSets, type KpiSetWithCount } from "../api/kpi";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { KpiSetPackageLink } from "../components/project/KpiSetPackageLink";
import { useAuthStore } from "../stores/auth-store";
import { celebrateOnce } from "../lib/celebrate";
import { createFocusRefetch } from "../lib/focus-refetch";
import {
  KPI_PROJECT_COPY,
  kpiSetFrameworkWarning,
  kpiSetOptionLabel,
  shouldConfirmWithoutKpiSet,
  showKpiSetDropdown,
  sortKpiSetsForFramework,
} from "../lib/project-kpi-guard";
import {
  buildPeriodOptions,
  fillTemplate,
  fiscalYearNote,
  hasQuarterlyQuestions,
  periodRangeLine,
  PERIOD_COPY,
} from "../lib/period-ui";

type Step = 1 | 2 | 3 | 4;

// Dönem hatası kod bazlı ayrılır (mesaj metnine bakılmaz) — adım 2'ye geri dönüp
// segmentin üstünde amber uyarı gösterilir.
const PERIOD_ERROR_CODES = ["quarterly_not_supported", "no_questions_for_period"];

export function ProjectNew(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canAssignOther = user?.permissions["projects.view_all"] === true;

  const [step, setStep] = useState<Step>(1);
  // Müşteri ClientPicker ile seçilir (sunucu aramalı — 200 kaydı aşan listede de çalışır).
  // `?clientId=` ile gelindiyse ad tekil çağrıyla tamamlanır (çip adı göstersin).
  const [selectedClient, setSelectedClient] = useState<ClientPickerItem | null>(null);
  const clientId = selectedClient?.id ?? "";
  const presetClientId = params.get("clientId");
  useEffect(() => {
    if (!presetClientId) return;
    let cancelled = false;
    getClientById(presetClientId)
      .then((c) => {
        if (!cancelled && !c.deletedAt) setSelectedClient(toPickerItem(c));
      })
      .catch(() => {
        // bulunamadı → alan boş kalır, kullanıcı seçer
      });
    return () => {
      cancelled = true;
    };
  }, [presetClientId]);
  const [frameworkVersionId, setFrameworkVersionId] = useState("");
  // M13: opsiyonel sektör paketi. Backend snapshot resolved view ile yazar.
  const [sectorPackageId, setSectorPackageId] = useState<string>("");
  const [sectorPackages, setSectorPackages] = useState<SectorPackageSummary[]>([]);
  // M16 K28: opsiyonel KPI seti (müşteriye bağlı). Set varken seçilmeden oluşturma → onay diyaloğu.
  const [kpiSetId, setKpiSetId] = useState<string>("");
  const [kpiSets, setKpiSets] = useState<KpiSetWithCount[]>([]);
  const [kpiConfirmOpen, setKpiConfirmOpen] = useState(false);
  // Finar Ö11 — rapor genellikle biten yıl için hazırlanır: varsayılan önceki takvim yılı.
  const [reportingYear, setReportingYear] = useState(new Date().getFullYear() - 1);
  // Raporlama dönemi — varsayılan her zaman "annual" (mevcut davranış).
  const [periodType, setPeriodType] = useState<PeriodType>("annual");
  // Framework değişimi dönemi sıfırladıysa adım 2'de tek seferlik muted not.
  const [periodResetNote, setPeriodResetNote] = useState(false);
  // 422 dönem hatası (kod bazlı) — segmentin üstünde amber uyarı.
  const [periodError, setPeriodError] = useState<string | null>(null);
  // Aynı müşteri + yıl + dönem + framework için proje var mı (engel değil, not).
  const [duplicateExists, setDuplicateExists] = useState(false);
  const [assignedTo, setAssignedTo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [frameworks, setFrameworks] = useState<PublishedFrameworkEntry[]>([]);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loadingRefs, setLoadingRefs] = useState(true);

  useEffect(() => {
    void Promise.all([
      api<PublishedFrameworksResponse>("/admin/frameworks/published"),
      canAssignOther
        ? api<UsersListResponse>("/admin/users?userKind=internal&isActive=true&limit=200").catch(
            () => ({
              users: [] as UserListItem[],
              roles: [],
            }),
          )
        : Promise.resolve({ users: [], roles: [] }),
    ])
      .then(([fw, us]) => {
        setFrameworks(fw.frameworks);
        setUsers(us.users);
        if (!assignedTo && user) setAssignedTo(user.id);
      })
      .finally(() => setLoadingRefs(false));
    // user.id ve canAssignOther sabit assume — mount'ta 1 defa yüklenir
  }, []);

  const selectedFramework = useMemo(
    () => frameworks.find((f) => f.version.id === frameworkVersionId) ?? null,
    [frameworks, frameworkVersionId],
  );
  const selectedSectorPackage = useMemo(
    () => sectorPackages.find((p) => p.id === sectorPackageId) ?? null,
    [sectorPackages, sectorPackageId],
  );

  // Framework seçilince ona bağlı published paketleri çek.
  // Dönem de sıfırlanır: yeni çerçeve çeyrekliği hiç desteklemiyor olabilir
  // (1C). Kullanıcı gerçekten bir çeyrek seçmişse tek seferlik not gösterilir.
  useEffect(() => {
    // periodType bilerek dep DEĞİL — framework değişim anındaki değeri okur
    // (dep olsaydı her dönem seçiminde efekt koşar, seçimi anında sıfırlardı).
    setPeriodResetNote(periodType !== "annual");
    setPeriodType("annual");
    setPeriodError(null);
    if (!frameworkVersionId) {
      setSectorPackages([]);
      setSectorPackageId("");
      return;
    }
    listPublishedSectorPackages(frameworkVersionId)
      .then((res) => setSectorPackages(res.packages))
      .catch(() => setSectorPackages([]));
    setSectorPackageId(""); // framework değişti, paket seçimini sıfırla
  }, [frameworkVersionId]);

  // M16 K28 — müşteri seçilince KPI setlerini çek; müşteri değişti → seçim sıfır.
  // Yetki yok / hata → boş liste (dropdown görünmez, diyalog açılmaz; mevcut akış birebir).
  useEffect(() => {
    setKpiSetId("");
    if (!clientId) {
      setKpiSets([]);
      return;
    }
    let cancelled = false;
    listKpiSets(clientId)
      .then((sets) => {
        if (!cancelled) setKpiSets(sets);
      })
      .catch(() => {
        if (!cancelled) setKpiSets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);
  // Yeni sekmede ("Sektör paketinden set kur") set kurulup dönülünce liste tazelensin; seçim korunur.
  useEffect(() => {
    if (!clientId) return;
    const r = createFocusRefetch(
      () => listKpiSets(clientId),
      setKpiSets,
      () => document.visibilityState === "hidden",
    );
    const refetch = r.trigger;
    window.addEventListener("focus", refetch);
    document.addEventListener("visibilitychange", refetch);
    return () => {
      r.cancel();
      window.removeEventListener("focus", refetch);
      document.removeEventListener("visibilitychange", refetch);
    };
  }, [clientId]);
  const selectedKpiSet = useMemo(
    () => kpiSets.find((s) => s.id === kpiSetId) ?? null,
    [kpiSets, kpiSetId],
  );
  // TODO-KPI-29: projenin çerçevesiyle eşleşen setler önce + "(uyumlu)"; farklı çerçeveli set
  // seçilirse amber not (engel yok, K28 diyaloğu aynen).
  const projectFrameworkId = selectedFramework?.frameworkId ?? null;
  const orderedKpiSets = useMemo(
    () => sortKpiSetsForFramework(kpiSets, projectFrameworkId),
    [kpiSets, projectFrameworkId],
  );
  const kpiFrameworkWarning = kpiSetFrameworkWarning(selectedKpiSet, {
    frameworkId: projectFrameworkId,
    frameworkName: selectedFramework?.frameworkName ?? null,
  });

  // Dönem seçenekleri — kapalı çerçevede hiçbir şey render edilmez (1C).
  const periodCounts = selectedFramework?.periodQuestionCounts ?? null;
  const showPeriodSegment =
    selectedFramework?.supportsQuarterly === true &&
    periodCounts !== null &&
    hasQuarterlyQuestions(periodCounts);
  const periodOptions = useMemo(
    () => (periodCounts ? buildPeriodOptions(periodCounts) : []),
    [periodCounts],
  );
  const periodNotes = periodOptions.map((o) => o.note).filter((n): n is string => n !== null);

  // Adım 4 — aynı müşteri+yıl+dönem+framework projesi var mı? Engel değil, not.
  useEffect(() => {
    if (step !== 4 || !clientId || !frameworkVersionId) {
      setDuplicateExists(false);
      return;
    }
    let aborted = false;
    const q = new URLSearchParams({
      clientId,
      frameworkVersionId,
      reportingYear: String(reportingYear),
      periodType,
      limit: "1",
    });
    void api<ProjectsListResponse>(`/projects?${q.toString()}`)
      .then((res) => {
        if (!aborted) setDuplicateExists(res.total > 0);
      })
      .catch(() => {
        if (!aborted) setDuplicateExists(false);
      });
    return () => {
      aborted = true;
    };
  }, [step, clientId, frameworkVersionId, reportingYear, periodType]);
  const selectedAssignee = useMemo(
    () => users.find((u) => u.id === assignedTo) ?? (user ? userToListItem(user) : null),
    [users, assignedTo, user],
  );

  const step1Valid = clientId && frameworkVersionId;
  const step2Valid = reportingYear >= 2000 && reportingYear <= 2100;
  // M17 T5 — mali yıl müşterisinde adım 2 notu (takvimde null → DOM değişmez)
  const fiscalNote = fiscalYearNote(reportingYear, selectedClient?.fiscalYearStartMonth);
  const step3Valid = assignedTo.length > 0;

  async function submit() {
    setSubmitting(true);
    setError(null);
    setPeriodError(null);
    try {
      const res = await api<{ project: { id: string } }>("/projects", {
        method: "POST",
        body: {
          clientId,
          frameworkVersionId,
          ...(sectorPackageId ? { sectorPackageId } : {}),
          ...(kpiSetId ? { kpiSetId } : {}),
          reportingYear,
          periodType,
          assignedTo,
        },
      });
      // M9-S4: ilk proje celebrate (subtle confetti burst)
      celebrateOnce("first-project");
      void navigate(`/projects/${res.project.id}`);
    } catch (err) {
      // Dönem hataları KOD bazlı ayrılır (mesaj metnine güvenilmez): kullanıcı
      // adım 2'ye geri alınır, segmentin üstünde ne yapacağını söyleyen uyarı çıkar.
      if (err instanceof ApiError && PERIOD_ERROR_CODES.includes(err.code)) {
        setPeriodError(PERIOD_COPY.createPeriodError);
        setStep(2);
        return;
      }
      setError(err instanceof ApiError ? err.message : "Proje oluşturulamadı");
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingRefs) {
    return (
      <div className="p-8" style={{ color: "var(--color-text-2)" }}>
        Referans veriler yükleniyor…
      </div>
    );
  }

  return (
    <div className="p-8 mx-auto" style={{ fontFamily: "var(--font-sans)", maxWidth: 820 }}>
      <Link to="/projects" className="text-sm" style={{ color: "var(--color-primary)" }}>
        ← Proje listesine dön
      </Link>
      <h1
        className="mt-4 text-3xl"
        style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
      >
        Yeni Proje
      </h1>
      <p className="text-sm mt-1 mb-6" style={{ color: "var(--color-text-2)" }}>
        4 adımda rapor projesi kurun — seçilen çerçeve projeye donmuş kopya olarak bağlanır.
      </p>

      <Stepper step={step} />

      {error ? (
        <div
          role="alert"
          className="mb-4 px-3 py-2 rounded-lg text-sm"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {error}
        </div>
      ) : null}

      {step === 1 ? (
        <div className="grid gap-5">
          <div>
            <label
              htmlFor="project-new-client"
              className="block mb-1.5 text-sm font-medium"
              style={{ color: "var(--color-text)" }}
            >
              Müşteri
            </label>
            <ClientPicker
              id="project-new-client"
              ariaLabel="Müşteri"
              value={selectedClient}
              onChange={setSelectedClient}
            />
          </div>
          <div>
            <label
              className="block mb-2 text-sm font-medium"
              style={{ color: "var(--color-text)" }}
            >
              Rapor Türü
            </label>
            {frameworks.length === 0 ? (
              <div
                className="p-4 rounded-lg text-sm"
                style={{
                  background: "var(--color-surface)",
                  border: "1px dashed var(--color-border)",
                  color: "var(--color-text-3)",
                }}
              >
                Henüz yayınlanmış çerçeve yok. Bir yönetici Çerçeve Yöneticisi'nde bir çerçeve
                yayınlamalı.
              </div>
            ) : (
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}
              >
                {frameworks.map((f) => {
                  const active = f.version.id === frameworkVersionId;
                  return (
                    <button
                      key={f.version.id}
                      onClick={() => setFrameworkVersionId(f.version.id)}
                      className="text-left rounded-xl p-4"
                      style={{
                        background: active ? "rgba(15,42,61,0.04)" : "var(--color-surface)",
                        border: active
                          ? "2px solid var(--color-primary)"
                          : "1px solid var(--color-border)",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: "1.05rem",
                          color: "var(--color-text)",
                          fontWeight: 600,
                        }}
                      >
                        {f.frameworkName}
                      </div>
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: "0.78rem",
                          color: "var(--color-text-3)",
                          fontFamily: "var(--font-mono)",
                        }}
                      >
                        {f.version.versionNumber} · {f.questionCount} soru · {f.sectionCount} bölüm
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* M13: opsiyonel sektör paketi dropdown — sadece framework seçildi
                ve ona bağlı yayında paket varsa görünür. */}
            {frameworkVersionId && sectorPackages.length > 0 && (
              <div className="mt-4">
                <label
                  className="block mb-1.5 text-sm font-medium"
                  style={{ color: "var(--color-text)" }}
                >
                  Sektör Paketi <span style={{ color: "var(--color-text-3)" }}>(opsiyonel)</span>
                </label>
                <select
                  value={sectorPackageId}
                  onChange={(e) => setSectorPackageId(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid var(--color-border)",
                    borderRadius: 6,
                    fontSize: "0.9rem",
                    maxWidth: 400,
                  }}
                >
                  <option value="">
                    {selectedFramework?.frameworkName ?? "Genel"} (paket yok)
                  </option>
                  {sectorPackages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <div
                  className="mt-1 text-xs"
                  style={{ color: "var(--color-text-3)", fontStyle: "italic" }}
                >
                  Sektör paketi: parent çerçevenin sektöre özel varyantı (bölüm/soru çıkarılmış veya
                  metin override edilmiş).
                </div>
              </div>
            )}

            {/* M16 K28: KPI seti dropdown'u — sektör paketi bloğundan SONRA (RK13); yalnız
                müşteri seçili ve müşterinin seti varken görünür. */}
            {showKpiSetDropdown({ clientId, sets: kpiSets }) && (
              <div className="mt-4">
                <label
                  htmlFor="project-kpi-set"
                  className="block mb-1.5 text-sm font-medium"
                  style={{ color: "var(--color-text)" }}
                >
                  {KPI_PROJECT_COPY.label}{" "}
                  <span style={{ color: "var(--color-text-3)" }}>{KPI_PROJECT_COPY.optional}</span>
                </label>
                <select
                  id="project-kpi-set"
                  value={kpiSetId}
                  onChange={(e) => setKpiSetId(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid var(--color-border)",
                    borderRadius: 6,
                    fontSize: "0.9rem",
                    maxWidth: 400,
                  }}
                >
                  <option value="">{KPI_PROJECT_COPY.noneOption}</option>
                  {orderedKpiSets.map((s) => (
                    <option key={s.id} value={s.id}>
                      {kpiSetOptionLabel(s, projectFrameworkId)}
                    </option>
                  ))}
                </select>
                {kpiFrameworkWarning ? (
                  <div
                    role="status"
                    className="mt-2 px-3 py-2 rounded-lg text-sm"
                    style={{
                      background: "var(--color-warning-bg)",
                      border: "1px solid var(--color-warning-border)",
                      color: "var(--color-warning-text)",
                      maxWidth: 400,
                    }}
                  >
                    {kpiFrameworkWarning}
                  </div>
                ) : null}
                <div
                  className="mt-1 text-xs"
                  style={{ color: "var(--color-text-3)", fontStyle: "italic" }}
                >
                  {KPI_PROJECT_COPY.hint}
                </div>
              </div>
            )}
          </div>
          <Nav
            canBack={false}
            canNext={Boolean(step1Valid)}
            onNext={() => setStep(2)}
            onCancel={() => void navigate("/projects")}
          />
        </div>
      ) : null}

      {step === 2 ? (
        <div className="grid gap-5">
          <div>
            <label
              className="block mb-1.5 text-sm font-medium"
              style={{ color: "var(--color-text)" }}
            >
              Raporlama Yılı
            </label>
            <select
              value={reportingYear}
              onChange={(e) => setReportingYear(Number(e.target.value))}
              className="w-full max-w-xs px-3 py-2.5 rounded-lg border"
              style={{
                background: "var(--color-surface)",
                borderColor: "var(--color-border)",
                fontSize: "0.9rem",
              }}
            >
              {/* #022 (Q21): mevcut yıl - 5 → mevcut yıl + 3. ProjectsList filter ile tutarlı. */}
              {Array.from({ length: 9 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            {/* Çeyreklik kapalıyken (ya da hiç çeyrek sorusu yokken) adım 2 bugünküyle
                birebir aynı kalır — yalnız bu cümle görünür. */}
            {periodType === "annual" ? (
              <p className="text-xs mt-2" style={{ color: "var(--color-text-3)" }}>
                Rapor bu yıla ait faaliyetleri kapsar.
              </p>
            ) : null}
            {periodResetNote ? (
              <p className="text-xs mt-2" style={{ color: "var(--color-text-3)" }}>
                {PERIOD_COPY.newPeriodReset}
              </p>
            ) : null}
            {fiscalNote ? (
              <p role="note" className="text-xs mt-2" style={{ color: "var(--color-text-2)" }}>
                {fiscalNote}
              </p>
            ) : null}
          </div>

          {selectedFramework?.supportsQuarterly === true && !showPeriodSegment ? (
            <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
              {PERIOD_COPY.newPeriodOnlyAnnual}
            </p>
          ) : null}

          {periodError ? (
            <div
              role="alert"
              className="px-3 py-2 rounded-lg text-sm"
              style={{
                background: "var(--color-warning-bg)",
                border: "1px solid var(--color-warning-border)",
                color: "var(--color-warning-text)",
              }}
            >
              {periodError}
            </div>
          ) : null}

          {showPeriodSegment ? (
            <div>
              <label
                className="block mb-1.5 text-sm font-medium"
                style={{ color: "var(--color-text)" }}
                id="period-segment-label"
              >
                {PERIOD_COPY.newPeriodLabel}
              </label>
              {/* Native radio grubu: ok tuşlarıyla gezinme tarayıcıdan gelir.
                  768–1023 px'te 2×2 (md), ≥1024 px'te tek satır 4 sütun (lg). */}
              <div
                role="radiogroup"
                aria-labelledby="period-segment-label"
                aria-describedby="period-range-line"
                className="grid grid-cols-2 lg:grid-cols-4 gap-2"
                style={{ maxWidth: 520 }}
              >
                {periodOptions.map((opt) => {
                  const active = opt.period === periodType;
                  return (
                    <label
                      key={opt.period}
                      className="flex items-center justify-center rounded-xl text-sm relative focus-within:outline focus-within:outline-2 focus-within:outline-offset-2"
                      style={{
                        outlineColor: "var(--color-primary)",
                        minHeight: 44,
                        padding: "0 10px",
                        background: active ? "rgba(15,42,61,0.04)" : "var(--color-surface)",
                        border: active
                          ? "2px solid var(--color-primary)"
                          : "1px solid var(--color-border)",
                        color: opt.disabled ? "var(--color-text-3)" : "var(--color-text)",
                        fontWeight: active ? 600 : 400,
                        cursor: opt.disabled ? "not-allowed" : "pointer",
                        opacity: opt.disabled ? 0.55 : 1,
                      }}
                    >
                      <input
                        type="radio"
                        name="periodType"
                        value={opt.period}
                        checked={active}
                        disabled={opt.disabled}
                        onChange={() => {
                          setPeriodType(opt.period);
                          setPeriodResetNote(false);
                          setPeriodError(null);
                        }}
                        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
              <p
                id="period-range-line"
                className="mt-2"
                style={{ fontSize: 13, color: "var(--color-text-2)" }}
              >
                {periodRangeLine(reportingYear, periodType)}
              </p>
              {periodNotes.map((n) => (
                <p key={n} className="text-xs mt-1" style={{ color: "var(--color-text-3)" }}>
                  {n}
                </p>
              ))}
              <p className="text-xs mt-2" style={{ color: "var(--color-text-3)" }}>
                {PERIOD_COPY.newPeriodHelp}
              </p>
            </div>
          ) : null}
          <Nav
            canBack
            canNext={step2Valid}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
            onCancel={() => void navigate("/projects")}
          />
        </div>
      ) : null}

      {step === 3 ? (
        <div className="grid gap-5">
          <div>
            <label
              className="block mb-1.5 text-sm font-medium"
              style={{ color: "var(--color-text)" }}
            >
              Sorumlu Danışman
            </label>
            {canAssignOther ? (
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                className="w-full max-w-md px-3 py-2.5 rounded-lg border"
                style={{
                  background: "var(--color-surface)",
                  borderColor: "var(--color-border)",
                  fontSize: "0.9rem",
                }}
              >
                <option value="">— seçin —</option>
                {users
                  // Finar Ö11 — yalnız aktif iç kullanıcı (BE de 422 assignee_invalid ile reddeder).
                  .filter((u) => u.userKind === "internal" && u.isActive)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.fullName} · {roleDisplayName(u.roleName)}
                    </option>
                  ))}
              </select>
            ) : (
              <div
                className="px-3 py-2.5 rounded-lg text-sm"
                style={{
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                  color: "var(--color-text)",
                }}
              >
                {user?.fullName} (siz) — proje size atanacak.
              </div>
            )}
          </div>
          <Nav
            canBack
            canNext={step3Valid}
            onBack={() => setStep(2)}
            onNext={() => setStep(4)}
            onCancel={() => void navigate("/projects")}
          />
        </div>
      ) : null}

      {step === 4 ? (
        <div>
          <div
            className="rounded-xl p-6 mb-4"
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
            }}
          >
            <h3
              className="mb-4"
              style={{
                fontFamily: "var(--font-display)",
                color: "var(--color-text)",
                fontSize: "1.05rem",
              }}
            >
              Özet
            </h3>
            <Row label="Müşteri" value={selectedClient?.name ?? "—"} />
            <Row
              label="Çerçeve"
              value={
                selectedFramework
                  ? `${selectedFramework.frameworkName} ${selectedFramework.version.versionNumber}`
                  : "—"
              }
            />
            <Row
              label="Kapsam"
              value={
                selectedFramework
                  ? periodType === "annual"
                    ? `${selectedFramework.sectionCount} bölüm · ${selectedFramework.questionCount} soru`
                    : fillTemplate(PERIOD_COPY.summaryScopePeriod, {
                        sections: selectedFramework.sectionCount,
                        questions: selectedFramework.periodQuestionCounts[periodType],
                      })
                  : "—"
              }
              {...(selectedFramework && periodType !== "annual"
                ? {
                    // Dönem sayıları çerçevenin tamamına göre hesaplanır; sektör
                    // paketinin hariç tutmaları bu sayılara YANSIMAZ (D5).
                    muted: `${fillTemplate(PERIOD_COPY.summaryScopeAnnual, {
                      n: selectedFramework.periodQuestionCounts.annual,
                    })}${selectedSectorPackage ? ` ${PERIOD_COPY.summaryScopePackageNote}` : ""}`,
                  }
                : {})}
            />
            {selectedSectorPackage && (
              <Row label="Sektör Paketi" value={selectedSectorPackage.name} />
            )}
            {kpiSets.length > 0 && (
              <Row
                label={KPI_PROJECT_COPY.label}
                value={selectedKpiSet?.name ?? KPI_PROJECT_COPY.summaryNone}
              />
            )}
            <Row
              label={PERIOD_COPY.newPeriodLabel}
              value={formatReportingPeriod(reportingYear, periodType)}
            />
            <Row label="Danışman" value={selectedAssignee?.fullName ?? "—"} />
            {duplicateExists ? (
              <div
                role="status"
                className="mt-3 px-3 py-2 rounded-lg text-sm"
                style={{
                  background: "var(--color-warning-bg)",
                  border: "1px solid var(--color-warning-border)",
                  color: "var(--color-warning-text)",
                }}
              >
                {PERIOD_COPY.summaryDuplicate}
              </div>
            ) : null}
          </div>
          <Nav
            canBack
            canNext={false}
            onBack={() => setStep(3)}
            onCancel={() => void navigate("/projects")}
            submitLabel={submitting ? "Oluşturuluyor…" : "Projeyi Oluştur ve Forma Geç"}
            onSubmit={() => {
              // K28: müşterinin seti varken set seçilmediyse önce onay; set yoksa doğrudan oluştur.
              if (shouldConfirmWithoutKpiSet({ sets: kpiSets, selectedKpiSetId: kpiSetId })) {
                setKpiConfirmOpen(true);
                return;
              }
              void submit();
            }}
            submitting={submitting}
          />
          <ConfirmDialog
            open={kpiConfirmOpen}
            title={KPI_PROJECT_COPY.confirmTitle}
            body={KPI_PROJECT_COPY.confirmBody}
            extra={<KpiSetPackageLink clientId={clientId} />}
            confirmLabel={KPI_PROJECT_COPY.confirmLabel}
            cancelLabel={KPI_PROJECT_COPY.cancelLabel}
            busy={submitting}
            onConfirm={() => {
              setKpiConfirmOpen(false);
              void submit();
            }}
            onCancel={() => setKpiConfirmOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}

function userToListItem(u: {
  id: string;
  email: string;
  fullName: string;
  roleId: string;
  roleName: string;
}): UserListItem {
  return {
    id: u.id,
    email: u.email,
    fullName: u.fullName,
    roleId: u.roleId,
    roleName: u.roleName,
    isActive: true,
    lastLoginAt: null,
    createdAt: new Date().toISOString(),
    // Danışman ataması listesi zaten dahili kullanıcılardan geliyor (client'lar bu
    // dropdown'da görünmez) — bu placeholder mapper için sabit "internal" yeterli.
    userKind: "internal",
  };
}

function Stepper({ step }: { step: Step }): JSX.Element {
  const steps = [
    { n: 1, label: "Çerçeve" },
    { n: 2, label: "Yıl" },
    { n: 3, label: "Danışman" },
    { n: 4, label: "Onayla" },
  ] as const;
  return (
    <div className="mb-8 flex items-center gap-2">
      {steps.map((s, i) => {
        const active = s.n === step;
        const done = s.n < step;
        return (
          <div key={s.n} className="flex items-center gap-2 flex-1">
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: active
                  ? "var(--color-primary)"
                  : done
                    ? "var(--color-success)"
                    : "var(--color-border)",
                color: active || done ? "var(--color-surface)" : "var(--color-text-3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              {done ? "✓" : s.n}
            </div>
            <span
              style={{
                fontSize: "0.85rem",
                color: active ? "var(--color-text)" : "var(--color-text-3)",
                fontWeight: active ? 500 : 400,
              }}
            >
              {s.label}
            </span>
            {i < steps.length - 1 ? (
              <div
                style={{
                  flex: 1,
                  height: 1,
                  background: done ? "var(--color-success)" : "var(--color-border)",
                  marginLeft: 8,
                }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Nav({
  canBack,
  canNext,
  onBack,
  onNext,
  onCancel,
  submitLabel,
  onSubmit,
  submitting,
}: {
  canBack: boolean;
  canNext: boolean;
  onBack?: () => void;
  onNext?: () => void;
  onCancel: () => void;
  submitLabel?: string | undefined;
  onSubmit?: () => void;
  submitting?: boolean | undefined;
}): JSX.Element {
  return (
    <div
      className="flex items-center justify-between gap-2 mt-4 pt-4"
      style={{ borderTop: "1px solid var(--color-border)" }}
    >
      <button
        type="button"
        onClick={onCancel}
        className="px-3 py-2 rounded-lg text-sm"
        style={{
          background: "transparent",
          border: "1px solid var(--color-border)",
          color: "var(--color-text-3)",
        }}
      >
        İptal
      </button>
      <div className="flex gap-2">
        {canBack ? (
          <button
            type="button"
            onClick={onBack}
            className="px-3 py-2 rounded-lg text-sm"
            style={{
              background: "transparent",
              border: "1px solid var(--color-border)",
              color: "var(--color-text)",
            }}
          >
            ← Geri
          </button>
        ) : null}
        {onSubmit ? (
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            {submitLabel}
          </button>
        ) : (
          <button
            type="button"
            disabled={!canNext}
            onClick={onNext}
            className="px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            İleri →
          </button>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  muted,
}: {
  label: string;
  value: string;
  /** Değerin altında ikinci satır (ör. "Yıllık: 45 soru"). */
  muted?: string;
}): JSX.Element {
  return (
    <div className="grid gap-1 mb-2" style={{ gridTemplateColumns: "140px 1fr" }}>
      <span style={{ color: "var(--color-text-3)", fontSize: "0.8rem" }}>{label}</span>
      <span style={{ color: "var(--color-text)", fontSize: "0.88rem" }}>
        {value}
        {muted ? (
          <span style={{ display: "block", color: "var(--color-text-3)", fontSize: "0.78rem" }}>
            {muted}
          </span>
        ) : null}
      </span>
    </div>
  );
}
